/**
 * Utility to serialize error information for Instana reporting.
 * Extracts complete error information including stack traces and cause chains.
 */

export interface SerializedError {
  message: string;
  stack?: string;
  name?: string;
  cause?: SerializedError;
  type?: string;
}

/**
 * Represents error-like objects that aren't Error instances (e.g., HttpErrorResponse).
 * These objects have error-like properties but may not be actual Error instances.
 */
export interface ErrorLike {
  message?: string;
  name?: string;
  stack?: string;
  status?: number;
  statusText?: string;
  url?: string;
  ok?: boolean;
  [key: string]: unknown; // Allow additional properties
}

/**
 * Serializes an error object to include all relevant information for error tracking.
 * Handles edge cases like non-Error objects, circular references, and nested errors.
 *
 * @param error The error to serialize
 * @param maxDepth Maximum depth for cause chain traversal (default: 5)
 * @returns Serialized error information
 */
export function serializeError(error: any, maxDepth = 5): SerializedError {
  if (maxDepth <= 0) {
    return {
      message: '[Error serialization depth limit reached]',
      type: 'SerializationLimit'
    };
  }

  // Handle null/undefined
  if (error == null) {
    return {
      message: String(error),
      type: 'NullError'
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const serialized: SerializedError = {
      message: error.message || '[No error message]',
      name: error.name || 'Error',
      type: error.constructor?.name || 'Error'
    };

    // Include stack trace if available
    if (error.stack) {
      serialized.stack = error.stack;
    }

    // Handle error cause (for nested errors)
    if ('cause' in error && error.cause && maxDepth > 0) {
      try {
        serialized.cause = serializeError(error.cause, maxDepth - 1);
      } catch (_e) {
        // Prevent circular reference issues
        serialized.cause = {
          message: '[Circular reference detected in error cause]',
          type: 'CircularReference'
        };
      }
    }

    return serialized;
  }

  // Handle objects with error-like properties
  if (typeof error === 'object') {
    try {
      const serialized: SerializedError = {
        message: error.message || error.toString() || '[Unknown error object]',
        name: error.name,
        type: error.constructor?.name || 'Object'
      };

      if (error.stack) {
        serialized.stack = String(error.stack);
      }

      if ('cause' in error && error.cause && maxDepth > 0) {
        try {
          serialized.cause = serializeError(error.cause, maxDepth - 1);
        } catch (_e) {
          serialized.cause = {
            message: '[Circular reference detected]',
            type: 'CircularReference'
          };
        }
      }

      return serialized;
    } catch (_e) {
      // Fallback for objects that can't be serialized
      return {
        message: '[Error object could not be serialized]',
        type: 'SerializationError'
      };
    }
  }

  // Handle primitives
  return {
    message: String(error),
    type: typeof error
  };
}

/**
 * Formats a SerializedError object for Instana reporting.
 * Internal function that formats already-serialized errors without re-serializing.
 *
 * @param serialized The serialized error to format
 * @returns Formatted error string with stack trace
 */
function formatSerializedError(serialized: SerializedError): string {
  let formatted = `[${serialized.name || serialized.type || 'Error'}] ${serialized.message}`;

  if (serialized.stack) {
    formatted += `\n${serialized.stack}`;
  }

  // Include cause chain if present (cause is already a SerializedError, so format it directly)
  if (serialized.cause) {
    formatted += `\nCaused by: ${formatSerializedError(serialized.cause)}`;
  }

  return formatted;
}

/**
 * Formats an error for Instana reporting by combining message and stack trace.
 *
 * @param error The error to format
 * @returns Formatted error string with stack trace
 */
export function formatErrorForInstana(error: any): string {
  const serialized = serializeError(error);
  return formatSerializedError(serialized);
}

/**
 * Checks if an object has error-like properties (message, name, status, etc.)
 * This helps identify error objects that aren't Error instances (e.g., HttpErrorResponse).
 * Requires multiple error-like properties to avoid false positives.
 */
function isErrorLikeObject(obj: any): boolean {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  // Must have multiple error-like properties to be considered an error-like object
  // This prevents false positives (e.g., objects with just a 'message' property)
  const hasStatus = 'status' in obj && typeof obj.status === 'number';
  const hasStatusText = 'statusText' in obj;
  const hasUrl = 'url' in obj && typeof obj.url === 'string';
  const hasName = 'name' in obj && typeof obj.name === 'string';
  const hasMessage = 'message' in obj && typeof obj.message === 'string';
  const hasOk = 'ok' in obj && typeof obj.ok === 'boolean';

  // HttpErrorResponse-like objects typically have: status, statusText, url, name, message, ok
  // Require at least 3 of these properties to be considered error-like
  const httpErrorLikeCount =
    (hasStatus ? 1 : 0) +
    (hasStatusText ? 1 : 0) +
    (hasUrl ? 1 : 0) +
    (hasName ? 1 : 0) +
    (hasMessage ? 1 : 0) +
    (hasOk ? 1 : 0);

  // If it looks like an HttpErrorResponse (has 3+ HTTP error properties)
  if (httpErrorLikeCount >= 3) {
    return true;
  }

  // Alternative: objects with name AND message (but not just message alone)
  // This catches other structured error objects
  if (hasName && hasMessage && !hasStatus) {
    // Additional check: name should be error-like (contains 'Error' or 'Exception')
    const nameStr = String(obj.name).toLowerCase();
    if (nameStr.includes('error') || nameStr.includes('exception')) {
      return true;
    }
  }

  return false;
}

/**
 * Converts an error-like object to an Error instance for Instana stack trace extraction.
 * Preserves all error information in the error message.
 *
 * @param errorLike The error-like object to convert
 * @returns An Error instance with all error information preserved
 */
export function convertToError(errorLike: any): Error {
  // Check if it's already an Error instance using multiple methods to handle edge cases
  // (e.g., cross-realm scenarios where instanceof might fail)
  if (
    errorLike instanceof Error ||
    Object.prototype.toString.call(errorLike) === '[object Error]'
  ) {
    return errorLike;
  }

  // Build a comprehensive error message from error-like objects
  let errorMessage = '';

  // Handle null/undefined safely
  if (errorLike == null) {
    errorMessage = 'Unknown error';
  } else if (errorLike.message) {
    errorMessage = errorLike.message;
  } else if (errorLike.name) {
    errorMessage = errorLike.name;
  } else {
    // When neither message nor name are present, construct message from context fields
    const contextParts: string[] = [];

    // Build HTTP error message from status, statusText, and url
    if (errorLike.status !== undefined) {
      const statusPart = `HTTP ${errorLike.status}`;
      if (errorLike.statusText) {
        contextParts.push(`${statusPart} ${errorLike.statusText}`);
      } else {
        contextParts.push(statusPart);
      }
    } else if (errorLike.statusText) {
      contextParts.push(errorLike.statusText);
    }

    // Add URL if available
    if (errorLike.url) {
      if (contextParts.length > 0) {
        contextParts.push(`at ${errorLike.url}`);
      } else {
        contextParts.push(`Error at ${errorLike.url}`);
      }
    }

    // If we have context parts, use them; otherwise fall back to "Unknown error"
    if (contextParts.length > 0) {
      errorMessage = contextParts.join(' ');
    } else {
      errorMessage = 'Unknown error';
    }
  }

  // Add additional context for HttpErrorResponse and similar objects
  // Only add context if we have a primary message (not constructed from context)
  const context: string[] = [];
  const hasPrimaryMessage = errorLike != null && (errorLike.message || errorLike.name);

  if (errorLike != null && hasPrimaryMessage) {
    if (errorLike.status !== undefined) {
      context.push(`Status: ${errorLike.status}`);
    }

    if (errorLike.statusText) {
      context.push(`StatusText: ${errorLike.statusText}`);
    }

    if (errorLike.url) {
      context.push(`URL: ${errorLike.url}`);
    }

    if (errorLike.name && errorLike.name !== 'Error') {
      context.push(`Type: ${errorLike.name}`);
    }
  }

  if (context.length > 0) {
    errorMessage = `${errorMessage} (${context.join(', ')})`;
  }

  const error = new Error(errorMessage);

  // Preserve original error name if available
  if (errorLike != null && errorLike.name) {
    error.name = errorLike.name;
  }

  // Preserve stack trace if available
  if (errorLike != null && errorLike.stack) {
    error.stack = errorLike.stack;
  }

  return error;
}

/**
 * Extracts error object from LogService payload.
 * LogService passes errors as [error, payload] array for error/fatal methods.
 * Also handles error-like objects (e.g., HttpErrorResponse) that aren't Error instances.
 *
 * @param payload The payload from LogService
 * @returns The error object (Error instance or error-like object) if found, null otherwise
 */
export function extractErrorFromPayload(payload: any): Error | ErrorLike | null {
  if (!payload) {
    return null;
  }

  // LogService.error() and LogService.fatal() pass [error, payload] as the payload
  if (Array.isArray(payload) && payload.length > 0) {
    const firstItem = payload[0];
    if (firstItem instanceof Error) {
      return firstItem;
    }
    // Handle error-like objects (e.g., HttpErrorResponse) that aren't Error instances
    if (isErrorLikeObject(firstItem)) {
      // Return error-like object as-is; conversion to Error instance happens in serializeError
      return firstItem as any;
    }
  }

  // Check if payload itself is an error
  if (payload instanceof Error) {
    return payload;
  }

  // Check if payload is an error-like object
  if (isErrorLikeObject(payload)) {
    return payload as any;
  }

  // Check if payload has an error property
  if (payload && typeof payload === 'object') {
    if (payload.error instanceof Error) {
      return payload.error;
    }
    // Handle nested error-like objects
    if (isErrorLikeObject(payload.error)) {
      return payload.error as any;
    }
  }

  return null;
}
